import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link2, Globe, Lock, Loader, Trash2, ChevronRight, ArrowLeft, Check, X } from "lucide-react";
import "src/components/buttons.css";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import type { RootState } from "src/store/RootStore";
import { selectPlan } from "src/store/slices/onboardingSelectors";
import {
  closeShareModal,
  fetchThreadAccess,
  switchToPublic,
  addRecipient,
  revokeRecipient,
  makePrivate,
} from "src/store/slices/sharedThreadSlice";
import { REQUEST_STATUS } from "src/domain/messages/requestStatus";

const AVATAR_COLORS = ["#EAE5D2", "#D2EAD9", "#EADCD2", "#D2DBEA", "#EAD4D2", "#DCD2EA"];

const ShareThreadModalSecured = () => {
  const dispatch = useDispatch<any>();
  const { showShareModal, fetchStatus, actionStatus } = useSelector(
    (state: RootState) => state.sharedSlice,
  );
  const { sharedThreadId, sharedType, sharedRecipients } = useSelector(
    (state: RootState) => state.sharedSlice.sharedAccessData ?? ({} as any),
  );

  const plan = useSelector(selectPlan);
  const ownerEmail = useSelector((state: RootState) => state.homeslice.email) as string | null;
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [activeAction, setActiveAction] = useState<"add" | "revoke" | "private" | "public" | null>(null);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  // Track viewport size for mobile bottom-sheet layout
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Calculate position from the share button
  const updatePosition = useCallback(() => {
    const btn = document.querySelector('[data-testid="share-thread-btn"]');
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    if (showShareModal && sharedThreadId) {
      dispatch(fetchThreadAccess(sharedThreadId));
      setShowSettings(false);
      updatePosition();
    }
  }, [showShareModal, sharedThreadId, dispatch, updatePosition]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    if (showShareModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareModal]); // eslint-disable-line

  const handleClose = () => {
    dispatch(closeShareModal());
    setEmailInput("");
    setShowSettings(false);
  };

  const shareUrl = sharedThreadId
    ? `${window.location.hostname}/chat/shared/${sharedThreadId}`
    : "";
  const fullShareUrl = sharedThreadId
    ? `${window.location.origin}/chat/shared/${sharedThreadId}`
    : "";

  const handleCopy = () => {
    navigator.clipboard
      .writeText(fullShareUrl)
      .then(() => toast.success("Link copied to clipboard!"))
      .catch(() => toast.error("Failed to copy link"));
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setActiveAction("add");
    dispatch(addRecipient({ threadId: sharedThreadId!, email })).finally(() => setActiveAction(null));
    setEmailInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRevoke = (email: string) => {
    setActiveAction("revoke");
    setRevokingEmail(email);
    dispatch(revokeRecipient({ threadId: sharedThreadId!, email, revokeAllThreads: false })).finally(() => {
      setActiveAction(null);
      setRevokingEmail(null);
    });
  };

  const handleSwitchToPublic = () => {
    setActiveAction("public");
    dispatch(switchToPublic(sharedThreadId!)).finally(() => {
      setActiveAction(null);
      setShowSettings(false);
    });
  };

  const handleSwitchToNamed = () => {
    setActiveAction("private");
    dispatch(makePrivate(sharedThreadId!)).finally(() => {
      setActiveAction(null);
      setShowSettings(false);
    });
  };

  const isPrivilegedPlan = plan.isPrivilegedChatUser();
  const isLoading = fetchStatus === REQUEST_STATUS.LOADING;
  const isActing = actionStatus === REQUEST_STATUS.LOADING;
  const currentSharedType = sharedType || "private";
  const recipientCount = sharedRecipients?.length || 0;

  const getInitials = (email: string) => email.charAt(0).toUpperCase();

  const accessLabel =
    currentSharedType === "public"
      ? "Anyone with the link can view"
      : "Only people you invite can view";

  const accessSubLabel =
    recipientCount > 0 ? `${recipientCount + 1} people` : undefined;

  const footerText =
    currentSharedType === "public"
      ? "Anyone with the link can read the conversation, including anything you say in it from now on."
      : "People with access can read the conversation, including anything you say in it from now on.";

  if (!showShareModal || isLoading) return null;

  // ── Copy link + footer (shared between both views) ──
  const CopyLinkSection = () => (
    <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
      <div
        className="flex items-center rounded-[8px] border border-[#E5E5E5] bg-[#FAFAFA] mb-3"
        style={{ height: "42px", paddingTop: "4px", paddingBottom: "4px", paddingLeft: "12px", paddingRight: "4px", gap: "8px" }}
      >
        <span className="truncate flex-1" style={{ fontFamily: "DM Sans", fontWeight: 400, fontSize: "12px", lineHeight: "16px", color: "#525252" }}>
          {shareUrl}
        </span>
        <button
          onClick={handleCopy}
          className="btn-base btn-secondary !rounded-[8px] !px-3 !py-1.5 flex-shrink-0"
          style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "20px" }}
        >
          <Link2 size={13} /> Copy link
        </button>
      </div>
      <p style={{ fontFamily: "DM Sans", fontWeight: 400, fontSize: "12px", lineHeight: "17px", color: "#7A7A7A" }}>
        {footerText}
      </p>
    </div>
  );

  // ── Modal content (shared between mobile and desktop layouts) ──
  const modalContent = (
    <>
      {/* ── Settings View ── */}
      {showSettings ? (
        <>
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 mt-2">
            <button
              onClick={() => setShowSettings(false)}
              className="flex items-center justify-center text-gray-800 hover:text-black"
              style={{ width: "32px", height: "32px", borderRadius: "999px", backgroundColor: "#F4F4F4" }}
            >
              <ArrowLeft size={18} />
            </button>
            <h2 className="text-[17px] font-semibold text-gray-900">Share conversation settings</h2>
            <button onClick={handleClose} className="ml-auto text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          {/* Who can open it */}
          <p className="text-[13px] text-gray-500 mb-2">Who can open it</p>

          <div className="border border-gray-200 rounded-[12px] overflow-hidden">
            {/* Named option */}
            <button
              onClick={currentSharedType === "public" ? handleSwitchToNamed : () => setShowSettings(false)}
              disabled={isActing}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-gray-100 ${isActing ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F4F4F4]"}`}
            >
              <Lock size={16} className="text-gray-500 flex-shrink-0" style={{ strokeWidth: 2.5 }} />
              <div className="flex-1">
                <p style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "20px" }} className="text-gray-900">
                  Only people invited can view
                </p>
                <p style={{ fontFamily: "DM Sans", fontWeight: 400, fontSize: "12px", lineHeight: "17px", color: "#7A7A7A" }} className="mt-0.5">
                  They sign in with the email you enter, or they see nothing.
                </p>
              </div>
              {activeAction === "private" ? (
                <Loader size={18} className="animate-spin text-gray-400 flex-shrink-0" />
              ) : currentSharedType !== "public" ? (
                <Check size={18} className="flex-shrink-0" style={{ color: "#2A4DFF", strokeWidth: 3 }} />
              ) : null}
            </button>

            {/* Public option */}
            {!isPrivilegedPlan ? (
              <button
                onClick={currentSharedType !== "public" ? handleSwitchToPublic : () => setShowSettings(false)}
                disabled={isActing}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isActing ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F4F4F4]"}`}
              >
                <Globe size={16} className="text-gray-500 flex-shrink-0" style={{ strokeWidth: 2.5 }} />
                <div className="flex-1">
                  <p style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "20px" }} className="text-gray-900">
                    Anyone with the link can view
                  </p>
                  <p style={{ fontFamily: "DM Sans", fontWeight: 400, fontSize: "12px", lineHeight: "17px", color: "#7A7A7A" }} className="mt-0.5">
                    No sign-in. Anyone you send it to can pass it on.
                  </p>
                </div>
                {activeAction === "public" ? (
                  <Loader size={18} className="animate-spin text-gray-400 flex-shrink-0" />
                ) : currentSharedType === "public" ? (
                  <Check size={18} className="flex-shrink-0" style={{ color: "#2A4DFF", strokeWidth: 3 }} />
                ) : null}
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 px-4 py-3 cursor-not-allowed">
                <Globe size={16} className="text-gray-500 flex-shrink-0" style={{ strokeWidth: 2.5 }} />
                <div className="flex-1">
                  <p style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "20px" }} className="text-gray-500">
                    Anyone with the link can view
                  </p>
                  <p style={{ fontFamily: "DM Sans", fontWeight: 400, fontSize: "12px", lineHeight: "17px", color: "#7A7A7A" }} className="mt-0.5">
                    Inhouse Counsel chats are privileged, so they can't be public.
                  </p>
                </div>
              </div>
            )}
          </div>

          <CopyLinkSection />
        </>
      ) : (
        /* ── Main View ── */
        <>
          {/* Header */}
          <div className="flex items-center justify-between mt-2 mb-4">
            <h2 className="text-[17px] font-semibold text-gray-900">Share this conversation</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          {sharedThreadId && (
            <>
              {/* Email invite */}
              <div className="flex gap-2 mb-4">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Invite people by email..."
                  className="flex-1 text-[14px] border border-gray-300 rounded-[8px] outline-none focus:border-[#2A4DFF] focus:ring-2 focus:ring-[#2A4DFF33] placeholder:text-gray-400"
                  style={{ height: "32px", paddingLeft: "6px", paddingRight: "6px" }}
                  disabled={isActing}
                />
                <button
                  onClick={handleAddEmail}
                  disabled={!emailInput.trim() || isActing}
                  className="btn-base btn-tertiary !rounded-[8px] !text-[14px]"
                  style={{ height: "32px", minWidth: "71px", paddingLeft: "16px", paddingRight: "16px" }}
                >
                  {activeAction === "add" ? <Loader size={14} className="animate-spin" /> : "Invite"}
                </button>
              </div>
              {emailError && (
                <p className="text-red-500 text-[12px] -mt-3 mb-3 ml-1">{emailError}</p>
              )}

              {/* Who has access */}
              <p className="text-[13px] text-gray-500 mb-1">Who has access</p>

              {/* Access type row — click opens settings view (pinned) */}
              <button
                onClick={() => setShowSettings(true)}
                className="w-full flex items-center hover:bg-[#F4F4F4] rounded-lg transition-colors px-2"
                style={{ height: "52px", paddingTop: "12px", paddingBottom: "12px", gap: "10px" }}
              >
                <div className="w-8 h-8 rounded-full bg-[#F0EBE4] flex items-center justify-center flex-shrink-0">
                  {currentSharedType === "public" ? (
                    <Globe size={18} className="text-gray-600" />
                  ) : (
                    <Lock size={18} className="text-gray-600" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "20px" }} className="text-gray-900">
                    {accessLabel}
                  </p>
                  {accessSubLabel && <p className="text-[12px] text-gray-400">{accessSubLabel}</p>}
                </div>
                <ChevronRight size={18} className="text-gray-400" />
              </button>

              {/* Scrollable people list: owner + recipients */}
              <div className="overflow-y-auto thin-scrollbar" style={{ maxHeight: "192px", scrollbarWidth: "thin", scrollbarColor: "#D4D4D4 transparent" }}>
                {/* Owner — only show for named/private, not public */}
                {currentSharedType !== "public" && (
                  <div className="flex items-center px-2" style={{ height: "48px", paddingTop: "8px", paddingBottom: "8px", gap: "10px" }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: AVATAR_COLORS[0] }}>
                      <span className="text-[13px] font-semibold text-[#141414]">{ownerEmail ? getInitials(ownerEmail) : "Y"}</span>
                    </div>
                    <p className="text-[14px] font-medium text-gray-900 flex-1">You</p>
                    <span className="text-[14px] text-gray-400">Owner</span>
                  </div>
                )}

                {/* Recipients */}
                {(sharedRecipients?.length ?? 0) > 0 &&
                  sharedRecipients!.map((recipient: { email: string; expires_at: string }, idx: number) => (
                    <div key={recipient.email} className="flex items-center px-2" style={{ height: "48px", paddingTop: "8px", paddingBottom: "8px", gap: "10px" }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: AVATAR_COLORS[(idx + 1) % AVATAR_COLORS.length] }}>
                        <span className="text-[13px] font-semibold text-[#141414]">
                          {getInitials(recipient.email)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-medium text-gray-900 truncate">
                          {recipient.email}{" "}
                          <span className="text-gray-400 font-normal">(Invited)</span>
                        </p>
                      </div>
                      <button
                        onClick={() => handleRevoke(recipient.email)}
                        disabled={isActing}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      >
                        {revokingEmail === recipient.email ? (
                          <Loader size={16} className="animate-spin text-gray-400" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  ))}
              </div>

              <CopyLinkSection />
            </>
          )}
        </>
      )}
    </>
  );

  // ── Mobile: bottom sheet with overlay ──
  if (isMobile) {
    return createPortal(
      <div
        className="fixed inset-0"
        style={{ zIndex: 1000 }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div
          ref={dropdownRef}
          className="absolute bottom-0 left-0 right-0 bg-white"
          style={{
            borderRadius: "16px 16px 0 0",
            padding: "16px 20px",
            paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          {modalContent}
        </div>
      </div>,
      document.body,
    );
  }

  // ── Desktop: dropdown positioned near share button ──
  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-white"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
        width: "480px",
        borderRadius: "12px",
        border: "1px solid #E5E5E5",
        boxShadow: "0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
        padding: "16px 20px",
        zIndex: 1000,
      }}
    >
      {modalContent}
    </div>,
    document.body,
  );
};

export default ShareThreadModalSecured;
